import {
  CancellationError,
  CodedError,
  DevelopmentError,
  ServerEmptyResponseError,
  ServerResponseError,
} from '@/API/base/networking-errors'
import { baseAPIURL } from '@/API/base/environment'
import { getAuthToken } from '@/services/auth'
import axios from 'axios'

/**
 * This is our main HTTP client, used throughout the API files. Will have Authorization automatically
 * set as a header once the user logs in. DO NOT use for external requests, only for internal to our
 * API requests
 * @type {AxiosClient}
 */
const HTTP = axios.create({
  baseURL: baseAPIURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000, // Current timeout is 60s, we'll make it smaller as we speed up the product
})

/**
 * Set up some custom error handling here for ALL network requests.
 * @throws {CancellationError} If the request was cancelled by us or the user's browser
 * @throws {ServerResponseError} If the response's status code is outside the 2xx range
 * @throws {CodedError} If the error had a code but no response object
 * @throws {ServerEmptyResponseError} If there's no response but the request was made properly
 * @throws {DevelopmentError} If setting up the request itself triggered the error
 * @return {Response}          The response object if successful
 */
const handleErrors = (error) => {
  if (axios.isCancel(error)) {
    throw new CancellationError(error)
  } else if (error.response) {
    throw new ServerResponseError(error.request, error.response)
  } else if (error.code) {
    throw new CodedError(error)
  } else if (error.request) {
    throw new ServerEmptyResponseError(error)
  } else {
    throw new DevelopmentError(error)
  }
}

const getHeaders = () => {
  const headers = { 'Content-Type': 'application/json' }
  const authToken = getAuthToken()

  if (authToken) {
    headers['Authorization'] = `Token ${authToken}`
  }

  return headers
}

// Set up interceptors
HTTP?.interceptors.response.use((response) => response, handleErrors)
HTTP?.interceptors.request.use(
  (config) => {
    const headers = getHeaders()

    // Merge the dynamic headers with the existing ones
    config.headers = { ...config.headers, ...headers }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export { HTTP }
